<script setup lang="ts">
interface Props {
  modelValue?: boolean
  title?: string
  closeText?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  closeText: '閉じる',
})

const emit = defineEmits<{ 'update:modelValue': [boolean] }>()

const visible = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
})

// スクリーンリーダーがダイアログのラベルとしてタイトルを読み上げるための ID
const titleId = useId()
</script>

<template>
  <v-dialog
    v-model="visible"
    :max-width="480"
    width="92dvw"
    scrollable
    :aria-labelledby="title ? titleId : undefined"
  >
    <v-card class="mobile-dialog">
      <v-card-title
        v-if="title"
        :id="titleId"
        class="mobile-dialog__title"
      >
        {{ title }}
      </v-card-title>
      <v-card-text class="mobile-dialog__body">
        <slot />
      </v-card-text>
      <!-- サービス本体のダイアログと同じ並び: とじるは左、次へ・アクションは右（2026-10-05 doi） -->
      <v-card-actions class="mobile-dialog__actions">
        <v-btn
          class="mobile-dialog__close"
          variant="outlined"
          @click="visible = false"
        >
          {{ closeText }}
        </v-btn>
        <v-spacer />
        <slot name="actions" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.mobile-dialog {
  font-family: "Noto Sans JP", sans-serif;
  /* 淡いピンクベージュの羊皮紙 — 黄色味を抑え、暖色だが「黄ばみ過ぎ」回避 */
  background: linear-gradient(135deg, #f5e8d6 0%, #ecdbc1 100%);
  color: #2a1f10;
  border: 1px solid rgba(140, 100, 50, 0.35);
}

.mobile-dialog__title {
  /* 紺色のリボン風ヘッダー — タイトルだけ強い色で全体の単調さを破る */
  font-family: "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", "Noto Serif", serif;
  font-weight: 700;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
  margin: 0;
  padding: 12px 20px;
  background: linear-gradient(135deg, #2c3e5a 0%, #1f2c44 100%);
  color: #f4e4bf;
  border-bottom: 2px solid #c9a440;
}

.mobile-dialog__body {
  padding: 8px 20px 16px;
  font-size: 0.95rem;
  line-height: 1.6;
}

.mobile-dialog__actions {
  padding: 8px 12px 12px;
  /* ヘッダー(濃紺)と対称に、フッターは紺の薄いオーバーレイ＋金色の上罫線 */
  background: rgba(44, 62, 90, 0.08);
  border-top: 1px solid #8b6f24;
}

.mobile-dialog__close {
  /* 羊皮紙の世界観に合わせた茶系のボタン — Vuetify default の青を上書き */
  color: #5b3a1a;
  border-color: rgba(91, 58, 26, 0.5);
  /* タイトル・寸評と統一感のある明朝体 */
  font-family: "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", "Noto Serif", serif;
  letter-spacing: 0.1em;
}
</style>
